import { Vector3 } from 'three'
import type { Brick, BrickType } from '@models/brick'
import { BRICK_CONSTANTS, getBrickDimensions } from './brickGeometry'

/**
 * Collision Detection Utilities
 *
 * Provides utilities for detecting collisions between bricks during placement.
 * Used to prevent placing bricks on top of each other (before stud-based
 * vertical stacking is implemented in Task 2.1.2).
 *
 * Current implementation:
 * - AABB (Axis-Aligned Bounding Box) collision detection
 * - Supports all brick sizes (1x1, 1x2, 2x2, 2x4, 1x4)
 * - Accounts for brick rotation (90°/270° swap width/length)
 * - O(n) complexity: checks against all existing bricks
 * - For >100 bricks, consider spatial partitioning (Task 2.2.1)
 *
 * Coordinate system:
 * - X: horizontal (left-right)
 * - Y: vertical (up-down)
 * - Z: horizontal (forward-back)
 * - Grid spacing: 0.8 units (BRICK_CONSTANTS.UNIT_SIZE)
 */

/**
 * Get brick half-dimensions accounting for rotation
 * At 90° and 270°, width and length are swapped
 *
 * @param type - The brick type
 * @param rotation - Rotation in degrees (0, 90, 180, 270)
 * @returns Object with halfWidth, halfHeight, halfLength
 */
function getBrickHalfDimensions(
  type: BrickType,
  rotation: number
): { halfWidth: number; halfHeight: number; halfLength: number } {
  const dims = getBrickDimensions(type)
  const halfHeight = dims.height / 2

  // At 90° and 270°, width and length are swapped
  if (rotation === 90 || rotation === 270) {
    return {
      halfWidth: dims.length / 2, // Swapped
      halfHeight,
      halfLength: dims.width / 2, // Swapped
    }
  }

  // At 0° and 180°, use dimensions as-is
  return {
    halfWidth: dims.width / 2,
    halfHeight,
    halfLength: dims.length / 2,
  }
}

/**
 * Check if a brick at the given position would collide with any existing bricks
 *
 * Uses 3D bounding box collision detection:
 * - Supports all brick types (1x1, 1x2, 2x2, 2x4, 1x4)
 * - Accounts for brick rotation (dimensions swap at 90°/270°)
 * - Position represents the center of the brick
 * - Returns true if ANY existing brick overlaps with the proposed position
 *
 * Collision criteria:
 * Two boxes collide if they overlap on ALL three axes (X, Y, Z)
 *
 * @param position - The center position where we want to place a new brick
 * @param newBrickType - The type of brick being placed
 * @param newBrickRotation - The rotation of the new brick in degrees
 * @param existingBricks - Map of all currently placed bricks
 * @returns true if collision detected, false if position is clear
 *
 * @example
 * const position = new Vector3(0.4, 0.48, 0.4) // Grid position
 * const collides = checkBrickCollision(position, '2x2', 0, bricks)
 * if (collides) {
 *   console.warn('Cannot place brick - position occupied')
 * }
 */
export function checkBrickCollision(
  position: Vector3,
  newBrickType: BrickType,
  newBrickRotation: number,
  existingBricks: Map<string, Brick>
): boolean {
  // Early exit if no bricks placed yet
  if (existingBricks.size === 0) return false

  // Get dimensions of the new brick accounting for rotation
  const newBrickHalfDims = getBrickHalfDimensions(newBrickType, newBrickRotation)

  // Calculate bounds of the new brick
  const newBrickMin = {
    x: position.x - newBrickHalfDims.halfWidth,
    y: position.y - newBrickHalfDims.halfHeight,
    z: position.z - newBrickHalfDims.halfLength,
  }

  const newBrickMax = {
    x: position.x + newBrickHalfDims.halfWidth,
    y: position.y + newBrickHalfDims.halfHeight,
    z: position.z + newBrickHalfDims.halfLength,
  }

  // Check collision against all existing bricks
  for (const brick of existingBricks.values()) {
    // Get dimensions of existing brick accounting for its rotation
    const existingBrickHalfDims = getBrickHalfDimensions(brick.type, brick.rotation)

    const existingBrickMin = {
      x: brick.position.x - existingBrickHalfDims.halfWidth,
      y: brick.position.y - existingBrickHalfDims.halfHeight,
      z: brick.position.z - existingBrickHalfDims.halfLength,
    }

    const existingBrickMax = {
      x: brick.position.x + existingBrickHalfDims.halfWidth,
      y: brick.position.y + existingBrickHalfDims.halfHeight,
      z: brick.position.z + existingBrickHalfDims.halfLength,
    }

    // AABB collision check: boxes collide if they overlap on ALL axes
    const overlapX =
      newBrickMin.x < existingBrickMax.x && newBrickMax.x > existingBrickMin.x
    const overlapY =
      newBrickMin.y < existingBrickMax.y && newBrickMax.y > existingBrickMin.y
    const overlapZ =
      newBrickMin.z < existingBrickMax.z && newBrickMax.z > existingBrickMin.z

    // If overlapping on all three axes, we have a collision
    if (overlapX && overlapY && overlapZ) {
      return true // Collision detected
    }
  }

  return false // No collision - position is clear
}

/**
 * Check if a position is valid for brick placement
 *
 * Combines multiple validation checks:
 * 1. Collision detection (no overlapping bricks)
 *    - Exception: Allow vertical stacking when snapped to studs
 * 2. Future: Height bounds (prevent building too high)
 * 3. Future: Area bounds (prevent placing outside play area)
 *
 * @param position - The position to validate
 * @param brickType - The type of brick being placed
 * @param brickRotation - The rotation of the brick in degrees
 * @param existingBricks - Map of all currently placed bricks
 * @param isSnappedToStud - If true, allows vertical overlap (intentional stacking)
 * @returns true if position is valid for placement
 */
export function isValidPlacement(
  position: Vector3,
  brickType: BrickType,
  brickRotation: number,
  existingBricks: Map<string, Brick>,
  isSnappedToStud: boolean = false
): boolean {
  // If snapped to a stud, we're intentionally stacking vertically
  // This means vertical overlap is expected and valid
  if (isSnappedToStud) {
    // Check for horizontal collision only (same Y level)
    // Allow bricks at different heights even if X/Z overlap
    const horizontalCollision = checkHorizontalCollision(
      position,
      brickType,
      brickRotation,
      existingBricks
    )
    return !horizontalCollision
  }

  // For ground placement, check full 3D collision
  if (checkBrickCollision(position, brickType, brickRotation, existingBricks)) {
    return false
  }

  // Future validations can be added here:
  // - Height limit check: position.y < MAX_BUILD_HEIGHT
  // - Area bounds check: within play area bounds
  // - Physics stability check: has support below

  return true
}

/**
 * Check for collision with bricks at the same height level
 *
 * Used when stacking vertically - we only want to prevent
 * placing multiple bricks at the same height and X/Z position.
 *
 * @param position - The position to check
 * @param brickType - The type of brick being placed
 * @param brickRotation - The rotation of the brick in degrees
 * @param existingBricks - All placed bricks
 * @returns true if collision detected at same height
 */
function checkHorizontalCollision(
  position: Vector3,
  brickType: BrickType,
  brickRotation: number,
  existingBricks: Map<string, Brick>
): boolean {
  if (existingBricks.size === 0) return false

  // Get dimensions of the new brick accounting for rotation
  const newBrickHalfDims = getBrickHalfDimensions(brickType, brickRotation)

  // Calculate bounds of the new brick
  const newBrickMin = {
    x: position.x - newBrickHalfDims.halfWidth,
    y: position.y - newBrickHalfDims.halfHeight,
    z: position.z - newBrickHalfDims.halfLength,
  }

  const newBrickMax = {
    x: position.x + newBrickHalfDims.halfWidth,
    y: position.y + newBrickHalfDims.halfHeight,
    z: position.z + newBrickHalfDims.halfLength,
  }

  // Check collision only with bricks at similar height
  for (const brick of existingBricks.values()) {
    // Skip bricks at different heights (vertical stacking is OK)
    const heightDifference = Math.abs(brick.position.y - position.y)
    if (heightDifference > BRICK_CONSTANTS.BRICK_HEIGHT * 0.5) {
      continue // Different level, no collision
    }

    // Get dimensions of existing brick accounting for its rotation
    const existingBrickHalfDims = getBrickHalfDimensions(brick.type, brick.rotation)

    const existingBrickMin = {
      x: brick.position.x - existingBrickHalfDims.halfWidth,
      y: brick.position.y - existingBrickHalfDims.halfHeight,
      z: brick.position.z - existingBrickHalfDims.halfLength,
    }

    const existingBrickMax = {
      x: brick.position.x + existingBrickHalfDims.halfWidth,
      y: brick.position.y + existingBrickHalfDims.halfHeight,
      z: brick.position.z + existingBrickHalfDims.halfLength,
    }

    // AABB collision check on X and Z only
    const overlapX =
      newBrickMin.x < existingBrickMax.x && newBrickMax.x > existingBrickMin.x
    const overlapZ =
      newBrickMin.z < existingBrickMax.z && newBrickMax.z > existingBrickMin.z

    // If overlapping horizontally at same height level, we have a collision
    if (overlapX && overlapZ) {
      return true
    }
  }

  return false
}
